import { Link } from "react-router-dom";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import {
  adminActivity,
  adminKpis,
  cityMix,
  serviceMix,
  todaysBookings,
  topCleaners,
} from "@/mocks/admin";
import RevenueChart from "./components/RevenueChart";

const statusBadge: Record<string, string> = {
  scheduled: "bg-secondary-100 text-secondary-800",
  in_progress: "bg-primary-100 text-primary-800",
  completed: "bg-accent-100 text-accent-800",
  cancelled: "bg-background-200 text-foreground-700",
  pending: "bg-accent-100 text-accent-800",
};

const statusLabel: Record<string, string> = {
  scheduled: "Scheduled",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
  pending: "Needs cleaner",
};

const toneStyles: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-800",
  secondary: "bg-secondary-100 text-secondary-800",
  danger: "bg-accent-100 text-accent-900",
};

export default function AdminOverviewPage() {
  return (
    <AdminLayout
      title="Operations overview"
      subtitle="Tuesday, June 17 · everything happening across PureNest today."
      action={
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button className="hidden sm:inline-flex items-center gap-1.5 bg-background-50 hover:bg-background-200 border border-background-200 text-foreground-800 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
            <i className="ri-download-line"></i> Export
          </button>
          <button className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 pl-4 pr-2 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
            New booking
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-50">
              <i className="ri-add-line text-primary-600 text-sm"></i>
            </span>
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {adminKpis.map((k) => (
            <div
              key={k.id}
              className="bg-background-50 border border-background-200 rounded-xl p-4 md:p-5"
            >
              <div className="flex items-start justify-between">
                <div
                  className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg ${k.tint}`}
                >
                  <i className={`${k.icon} text-base md:text-lg`}></i>
                </div>
                <span
                  className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                    k.trend === "up"
                      ? "bg-primary-100 text-primary-700"
                      : "bg-background-200 text-foreground-700"
                  }`}
                >
                  <i
                    className={
                      k.trend === "up" ? "ri-arrow-up-line" : "ri-arrow-down-line"
                    }
                  ></i>
                  {k.delta}
                </span>
              </div>
              <p className="mt-3 text-xl md:text-2xl font-heading font-semibold text-foreground-950">
                {k.value}
              </p>
              <p className="text-xs text-foreground-600 mt-0.5">{k.label}</p>
              <p className="text-[11px] text-foreground-500 mt-1">{k.hint}</p>
            </div>
          ))}
        </div>

        {/* Revenue + Service mix */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <div className="lg:col-span-2 bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-xs font-medium text-primary-700 uppercase tracking-wider">
                  Revenue
                </p>
                <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mt-1">
                  $221,020 last 6 months
                </h3>
                <p className="text-xs text-foreground-600 mt-1">
                  Trending up 18.4% vs. the previous period.
                </p>
              </div>
              <div className="flex items-center gap-1 bg-background-100 p-1 rounded-full">
                {["1M", "3M", "6M", "1Y"].map((t, i) => (
                  <button
                    key={t}
                    className={`text-[11px] px-3 py-1 rounded-full font-medium whitespace-nowrap cursor-pointer ${
                      i === 2
                        ? "bg-foreground-950 text-background-50"
                        : "text-foreground-700 hover:text-foreground-950"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <RevenueChart />
          </div>

          <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                Service mix
              </h3>
              <span className="text-[11px] text-foreground-500">This month</span>
            </div>
            <div className="space-y-3">
              {serviceMix.map((s) => (
                <div key={s.label}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-foreground-800 font-medium">{s.label}</span>
                    <span className="text-foreground-600">{s.share}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-background-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${s.tint}`}
                      style={{ width: `${s.share}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-5 border-t border-background-200">
              <p className="text-xs font-medium text-foreground-700 mb-3 uppercase tracking-wider">
                By city
              </p>
              <ul className="space-y-2">
                {cityMix.map((c) => (
                  <li
                    key={c.label}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="text-foreground-800">{c.label}</span>
                    <span className="text-foreground-600 font-medium">{c.share}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Today's bookings + activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <div className="lg:col-span-2 bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
            <div className="px-5 md:px-6 py-4 md:py-5 border-b border-background-200 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950">
                  Today's bookings
                </h3>
                <p className="text-xs text-foreground-600 mt-0.5">
                  {todaysBookings.length} jobs scheduled · 1 needs a cleaner
                </p>
              </div>
              <Link
                to="/admin/bookings"
                className="text-sm font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap"
              >
                Manage all <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-foreground-500 bg-background-100/60">
                    <th className="px-6 py-3 font-medium">Booking</th>
                    <th className="px-3 py-3 font-medium">Customer</th>
                    <th className="px-3 py-3 font-medium">Service</th>
                    <th className="px-3 py-3 font-medium">Cleaner</th>
                    <th className="px-3 py-3 font-medium">Time</th>
                    <th className="px-3 py-3 font-medium text-right">Amount</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {todaysBookings.map((b) => (
                    <tr
                      key={b.id}
                      className="border-t border-background-200 hover:bg-background-100/60 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-3 font-medium text-foreground-950">{b.id}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <img
                            src={b.customerAvatar}
                            alt={b.customer}
                            className="w-8 h-8 rounded-full object-cover object-top"
                          />
                          <div className="min-w-0">
                            <p className="text-foreground-950 truncate">{b.customer}</p>
                            <p className="text-[11px] text-foreground-500 truncate">
                              {b.address}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-foreground-800 whitespace-nowrap">
                        {b.service}
                      </td>
                      <td className="px-3 py-3 text-foreground-800 whitespace-nowrap">
                        {b.cleaner === "—" ? (
                          <span className="text-accent-700 font-medium">Unassigned</span>
                        ) : (
                          b.cleaner
                        )}
                      </td>
                      <td className="px-3 py-3 text-foreground-800 whitespace-nowrap">
                        {b.time}
                      </td>
                      <td className="px-3 py-3 text-right text-foreground-950 font-medium">
                        ${b.amount}
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${statusBadge[b.status]}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                          {statusLabel[b.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <ul className="md:hidden divide-y divide-background-200">
              {todaysBookings.map((b) => (
                <li key={b.id} className="p-4 flex items-center gap-3">
                  <img
                    src={b.customerAvatar}
                    alt={b.customer}
                    className="w-11 h-11 rounded-full object-cover object-top flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-medium text-foreground-950 truncate">
                        {b.customer}
                      </p>
                      <span className="text-[10px] text-foreground-500">{b.id}</span>
                    </div>
                    <p className="text-xs text-foreground-600 truncate">
                      {b.service} · {b.time}
                    </p>
                    <p className="text-[11px] text-foreground-500 truncate">
                      {b.cleaner === "—" ? "Unassigned" : b.cleaner}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-foreground-950">${b.amount}</p>
                    <span
                      className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${statusBadge[b.status]}`}
                    >
                      {statusLabel[b.status]}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                Activity feed
              </h3>
              <button className="text-xs text-foreground-600 hover:text-foreground-900 whitespace-nowrap cursor-pointer">
                Mark all read
              </button>
            </div>
            <ul className="space-y-4">
              {adminActivity.map((a) => (
                <li key={a.id} className="flex items-start gap-3">
                  <span
                    className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 ${toneStyles[a.tone]}`}
                  >
                    <i className={`${a.icon} text-sm`}></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-950 leading-tight">
                      {a.title}
                    </p>
                    <p className="text-xs text-foreground-600 leading-relaxed mt-0.5">
                      {a.description}
                    </p>
                    <p className="text-[11px] text-foreground-500 mt-0.5">{a.time}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Top cleaners */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                Top performing cleaners
              </h3>
              <p className="text-xs text-foreground-600 mt-0.5">
                Ranked by jobs completed this month.
              </p>
            </div>
            <Link
              to="/admin/cleaners"
              className="text-sm font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap"
            >
              Full roster <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
            {topCleaners.map((c, i) => (
              <div
                key={c.id}
                className="relative p-4 rounded-xl bg-background-100 border border-background-200/60"
              >
                <span className="absolute top-3 right-3 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-background-50 text-foreground-700 border border-background-200">
                  #{i + 1}
                </span>
                <img
                  src={c.image}
                  alt={c.name}
                  className="w-14 h-14 rounded-xl object-cover object-top"
                />
                <p className="mt-3 text-sm font-medium text-foreground-950 truncate">
                  {c.name}
                </p>
                <p className="text-[11px] text-foreground-600 truncate">{c.role}</p>
                <div className="mt-3 pt-3 border-t border-background-200/80 grid grid-cols-3 gap-1 text-center">
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">
                      {c.jobsMonth}
                    </p>
                    <p className="text-[10px] text-foreground-500">jobs</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{c.rating}</p>
                    <p className="text-[10px] text-foreground-500">rating</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">
                      ${(c.earnings / 1000).toFixed(1)}k
                    </p>
                    <p className="text-[10px] text-foreground-500">earned</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}